import { Fragment, useEffect, useMemo, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
  Text,
  Button,
  Badge,
  Tab,
  TabList,
  Spinner,
  MessageBar,
  MessageBarTitle,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  ClipboardCheckmarkRegular,
  CheckmarkCircleFilled,
  DocumentRegular,
} from '@fluentui/react-icons';
import type { Project } from '../services/project-service';
import type { MethodologyDocument } from '../services/document-service';
import { listTemplates, type TemplateManifestEntry } from '../services/template-service';
import { listCapabilities, type Capability } from '../services/capability-service';
import { useApiToken } from '../hooks/useApiToken';

interface AuditNavigatorPageProps {
  project: Project;
  documents: MethodologyDocument[];
  onBack: () => void;
  onSelectDocument: (doc: MethodologyDocument) => void;
}

type TabValue = 'matrix' | 'capabilities' | 'templates';

/**
 * Audit Navigator — surfaces Microsoft Specialisation Audit (V1.9.1) controls per
 * project. Renders a control-by-evidence matrix derived from each document's
 * auditControls metadata, lists controls without evidence, and (in the templates
 * tab) shows the full template catalogue with engagement-type and audit-control tags.
 */
export function AuditNavigatorPage({
  project,
  documents,
  onBack,
  onSelectDocument,
}: AuditNavigatorPageProps) {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState<TabValue>('matrix');
  const [templates, setTemplates] = useState<TemplateManifestEntry[] | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<Capability[] | null>(null);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [capabilitiesError, setCapabilitiesError] = useState<string | null>(null);
  const [expandedControl, setExpandedControl] = useState<string | null>(null);
  const [expandedCapability, setExpandedCapability] = useState<string | null>(null);
  const getToken = useApiToken();

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const token = await getToken();
      const items = await listTemplates(token);
      setTemplates(items);
    } catch (err) {
      setTemplatesError(err instanceof Error ? err.message : String(err));
    } finally {
      setTemplatesLoading(false);
    }
  }, [getToken]);

  const loadCapabilities = useCallback(async () => {
    setCapabilitiesLoading(true);
    setCapabilitiesError(null);
    try {
      const token = await getToken();
      const items = await listCapabilities(token);
      setCapabilities(items);
    } catch (err) {
      setCapabilitiesError(err instanceof Error ? err.message : String(err));
    } finally {
      setCapabilitiesLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (templates === null) {
      loadTemplates();
    }
  }, [templates, loadTemplates]);

  useEffect(() => {
    if (capabilities === null) {
      loadCapabilities();
    }
  }, [capabilities, loadCapabilities]);

  // Documents that carry at least one audit control.
  const evidenceDocs = useMemo(
    () => documents.filter(d => (d.auditControls?.length ?? 0) > 0),
    [documents],
  );

  // Distinct control IDs covered by the project's documents.
  const coveredControls = useMemo(() => {
    const set = new Set<string>();
    for (const d of documents) {
      for (const c of d.auditControls ?? []) set.add(c);
    }
    return [...set].sort(compareControlIds);
  }, [documents]);

  // Controls required for this engagement — derived from the union of auditControls
  // on all manifest templates filtered by the project's engagementTypes. Auto-updates
  // as new templates land.
  const requiredControls = useMemo(() => {
    if (!templates) return null;
    const projectTypes = new Set(project.engagementTypes ?? []);
    const set = new Set<string>();
    for (const t of templates) {
      const matchesProject =
        t.engagementType == null || projectTypes.has(t.engagementType);
      if (!matchesProject) continue;
      for (const c of t.auditControls ?? []) set.add(c);
    }
    return [...set].sort(compareControlIds);
  }, [templates, project.engagementTypes]);

  // Controls required by the engagement that no document in this project attests.
  const gapControls = useMemo(() => {
    if (!requiredControls) return null;
    const covered = new Set(coveredControls);
    return requiredControls.filter(c => !covered.has(c));
  }, [requiredControls, coveredControls]);

  // Rows for the matrix — every control referenced either by a document or by the
  // engagement's required-control set. Sorting keeps Module A above Module B.
  const matrixControls = useMemo(() => {
    const set = new Set<string>(coveredControls);
    for (const c of requiredControls ?? []) set.add(c);
    return [...set].sort(compareControlIds);
  }, [coveredControls, requiredControls]);

  const docsForControl = useCallback(
    (controlId: string) =>
      evidenceDocs.filter(d => d.auditControls?.includes(controlId)),
    [evidenceDocs],
  );

  // Phase D bridge: documents → capabilities → controls. A capability is
  // "evidenced" when at least one of its ISO control references is attested by a
  // project document. capability.controlReferences ∩ document.auditControls is
  // the join. Only capabilities that bridge to ≥1 covered control are shown —
  // the registry has 60+ capabilities, but a project only touches a handful.
  const capabilityRows = useMemo<CapabilityRow[]>(() => {
    if (!capabilities) return [];
    return capabilities
      .map(cap => {
        const controls = cap.controlReferences.map(controlId => ({
          controlId,
          documents: docsForControl(controlId),
        }));
        const evidencedControls = controls.filter(c => c.documents.length > 0);
        return { capability: cap, controls, evidencedControls };
      })
      .filter(row => row.evidencedControls.length > 0)
      .sort((a, b) =>
        a.capability.domain === b.capability.domain
          ? a.capability.id.localeCompare(b.capability.id)
          : a.capability.domain.localeCompare(b.capability.domain),
      );
  }, [capabilities, docsForControl]);

  const hasAnyEvidence = evidenceDocs.length > 0;

  return (
    <div className="page-root page-root--scrollable">
      <div className="page-title-bar">
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={onBack}
          title={intl.formatMessage({ id: 'auditNavigator.backButton.title' })}
        />
        <ClipboardCheckmarkRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">
          {intl.formatMessage({ id: 'auditNavigator.title' }, { name: project.name })}
        </Text>
      </div>

      <div className="audit-navigator-tabs">
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, d) => setActiveTab(d.value as TabValue)}
          size="small"
          className="mb-md"
        >
          <Tab value="matrix">{intl.formatMessage({ id: 'auditNavigator.tab.matrix' })}</Tab>
          <Tab value="capabilities">{intl.formatMessage({ id: 'auditNavigator.tab.capabilities' })}</Tab>
          <Tab value="templates">{intl.formatMessage({ id: 'auditNavigator.tab.templates' })}</Tab>
        </TabList>

        {activeTab === 'matrix' && (
          <MatrixView
            project={project}
            documents={documents}
            evidenceDocs={evidenceDocs}
            matrixControls={matrixControls}
            gapControls={gapControls}
            requiredControls={requiredControls}
            templatesLoading={templatesLoading}
            hasAnyEvidence={hasAnyEvidence}
            expandedControl={expandedControl}
            onToggleControl={controlId =>
              setExpandedControl(prev => (prev === controlId ? null : controlId))
            }
            docsForControl={docsForControl}
            onSelectDocument={onSelectDocument}
          />
        )}

        {activeTab === 'capabilities' && (
          <CapabilityMap
            rows={capabilityRows}
            loading={capabilitiesLoading}
            error={capabilitiesError}
            onRetry={loadCapabilities}
            hasAnyEvidence={hasAnyEvidence}
            expandedCapability={expandedCapability}
            onToggleCapability={capId =>
              setExpandedCapability(prev => (prev === capId ? null : capId))
            }
            onSelectDocument={onSelectDocument}
          />
        )}

        {activeTab === 'templates' && (
          <TemplateBrowser
            templates={templates}
            loading={templatesLoading}
            error={templatesError}
            onRetry={loadTemplates}
          />
        )}
      </div>
    </div>
  );
}

interface MatrixViewProps {
  project: Project;
  documents: MethodologyDocument[];
  evidenceDocs: MethodologyDocument[];
  matrixControls: string[];
  gapControls: string[] | null;
  requiredControls: string[] | null;
  templatesLoading: boolean;
  hasAnyEvidence: boolean;
  expandedControl: string | null;
  onToggleControl: (controlId: string) => void;
  docsForControl: (controlId: string) => MethodologyDocument[];
  onSelectDocument: (doc: MethodologyDocument) => void;
}

function MatrixView({
  project,
  documents,
  evidenceDocs,
  matrixControls,
  gapControls,
  templatesLoading,
  hasAnyEvidence,
  expandedControl,
  onToggleControl,
  docsForControl,
  onSelectDocument,
}: MatrixViewProps) {
  const intl = useIntl();
  // Empty state — no audit-control-tagged documents on this project.
  if (!hasAnyEvidence && matrixControls.length === 0) {
    return (
      <EmptyState project={project} totalDocuments={documents.length} loading={templatesLoading} />
    );
  }

  return (
    <>
      <Text size={300} className="text-muted mb-md" block>
        {intl.formatMessage({ id: 'auditNavigator.matrix.description' })}
      </Text>

      <div className="table-scroll-container">
        <Table size="small" aria-label={intl.formatMessage({ id: 'auditNavigator.matrix.tableAriaLabel' })}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ minWidth: 120 }}>{intl.formatMessage({ id: 'auditNavigator.matrix.col.control' })}</TableHeaderCell>
              {evidenceDocs.map(doc => (
                <TableHeaderCell key={doc.id} title={doc.title}>
                  <span style={{ writingMode: 'horizontal-tb', whiteSpace: 'normal' }}>
                    {doc.title}
                  </span>
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {matrixControls.map(controlId => {
              const isExpanded = expandedControl === controlId;
              const coveringDocs = docsForControl(controlId);
              const isGap = coveringDocs.length === 0;
              return (
                <Fragment key={controlId}>
                  <TableRow
                    onClick={() => onToggleControl(controlId)}
                    className="clickable"
                    aria-expanded={isExpanded}
                  >
                    <TableCell>
                      <span className="flex-row gap-xs" style={{ display: 'inline-flex' }}>
                        <Text weight="semibold">{controlId}</Text>
                        {isGap && (
                          <Badge size="extra-small" appearance="outline" color="warning">
                            {intl.formatMessage({ id: 'auditNavigator.matrix.gap' })}
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    {evidenceDocs.map(doc => {
                      const covers = doc.auditControls?.includes(controlId) ?? false;
                      return (
                        <TableCell
                          key={doc.id}
                          className="audit-matrix-cell"
                          aria-label={
                            covers
                              ? intl.formatMessage({ id: 'auditNavigator.matrix.cell.attests' }, { docTitle: doc.title, controlId })
                              : intl.formatMessage({ id: 'auditNavigator.matrix.cell.doesNotAttest' }, { docTitle: doc.title, controlId })
                          }
                        >
                          {covers ? (
                            <CheckmarkCircleFilled className="audit-matrix-cell-fill" />
                          ) : (
                            <span className="audit-matrix-cell-empty">—</span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={evidenceDocs.length + 1}>
                        <ControlDetail
                          controlId={controlId}
                          documents={coveringDocs}
                          onSelectDocument={onSelectDocument}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <GapsSection gapControls={gapControls} loading={templatesLoading} />
    </>
  );
}

interface ControlDetailProps {
  controlId: string;
  documents: MethodologyDocument[];
  onSelectDocument: (doc: MethodologyDocument) => void;
}

function ControlDetail({ controlId, documents, onSelectDocument }: ControlDetailProps) {
  const intl = useIntl();
  if (documents.length === 0) {
    return (
      <Text size={200} className="text-muted">
        {intl.formatMessage({ id: 'auditNavigator.controlDetail.noEvidence' }, { controlId })}
      </Text>
    );
  }
  return (
    <div className="audit-control-detail">
      <Text size={200} weight="semibold" className="mb-sm" block>
        {intl.formatMessage({ id: 'auditNavigator.controlDetail.attesting' }, { controlId })}
      </Text>
      <ul className="audit-control-detail-list">
        {documents.map(doc => (
          <li key={doc.id}>
            <Button
              appearance="transparent"
              size="small"
              icon={<DocumentRegular />}
              onClick={() => onSelectDocument(doc)}
              className="audit-control-detail-button"
            >
              {doc.title}
            </Button>
            <Text size={200} className="text-muted audit-control-detail-meta">
              ({doc.status} · v{doc.version})
            </Text>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface GapsSectionProps {
  gapControls: string[] | null;
  loading: boolean;
}

function GapsSection({ gapControls, loading }: GapsSectionProps) {
  const intl = useIntl();
  if (loading && gapControls === null) {
    return <Spinner size="tiny" label={intl.formatMessage({ id: 'auditNavigator.gaps.loading' })} />;
  }
  if (gapControls === null) return null;
  if (gapControls.length === 0) {
    return (
      <MessageBar intent="success">
        <MessageBarBody>
          <MessageBarTitle>{intl.formatMessage({ id: 'auditNavigator.gaps.allCovered.title' })}</MessageBarTitle>
          {intl.formatMessage({ id: 'auditNavigator.gaps.allCovered.body' })}
        </MessageBarBody>
      </MessageBar>
    );
  }
  return (
    <MessageBar intent="warning">
      <MessageBarBody>
        <MessageBarTitle>{intl.formatMessage({ id: 'auditNavigator.gaps.hasGaps.title' }, { count: gapControls.length })}</MessageBarTitle>
        {intl.formatMessage({ id: 'auditNavigator.gaps.hasGaps.body' })}
        <div className="flex-row flex-wrap gap-sm mt-sm">
          {gapControls.map(c => (
            <Badge key={c} size="small" appearance="outline" color="warning">
              {c}
            </Badge>
          ))}
        </div>
      </MessageBarBody>
    </MessageBar>
  );
}

interface TemplateBrowserProps {
  templates: TemplateManifestEntry[] | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

function TemplateBrowser({ templates, loading, error, onRetry }: TemplateBrowserProps) {
  const intl = useIntl();
  if (loading && templates === null) {
    return <div className="page-loading"><Spinner size="large" label={intl.formatMessage({ id: 'auditNavigator.templates.loading' })} /></div>;
  }
  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>{intl.formatMessage({ id: 'auditNavigator.templates.error.title' })}</MessageBarTitle>
          {error}
          <div className="mt-sm">
            <Button size="small" onClick={onRetry}>{intl.formatMessage({ id: 'auditNavigator.templates.error.retry' })}</Button>
          </div>
        </MessageBarBody>
      </MessageBar>
    );
  }
  if (!templates || templates.length === 0) {
    return (
      <div className="page-empty">
        <ClipboardCheckmarkRegular />
        <Text className="text-muted">{intl.formatMessage({ id: 'auditNavigator.templates.empty' })}</Text>
      </div>
    );
  }

  // Sort: universal templates first, then by engagement type, then by CAF phase.
  const sorted = [...templates].sort((a, b) => {
    const at = a.engagementType ?? '';
    const bt = b.engagementType ?? '';
    if (at !== bt) return at.localeCompare(bt);
    if (a.cafPhase !== b.cafPhase) return a.cafPhase.localeCompare(b.cafPhase);
    return a.title.localeCompare(b.title);
  });

  return (
    <>
      <Text size={300} className="text-muted mb-md" block>
        {intl.formatMessage({ id: 'auditNavigator.templates.description' })}
      </Text>
      <div className="table-scroll-container">
        <Table size="small" aria-label={intl.formatMessage({ id: 'auditNavigator.templates.tableAriaLabel' })}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{intl.formatMessage({ id: 'auditNavigator.templates.col.template' })}</TableHeaderCell>
              <TableHeaderCell>{intl.formatMessage({ id: 'auditNavigator.templates.col.cafPhase' })}</TableHeaderCell>
              <TableHeaderCell>{intl.formatMessage({ id: 'auditNavigator.templates.col.engagementType' })}</TableHeaderCell>
              <TableHeaderCell>{intl.formatMessage({ id: 'auditNavigator.templates.col.auditControls' })}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map(t => (
              <TableRow key={t.templateId}>
                <TableCell>
                  <div>
                    <Text weight="semibold">{t.title}</Text>
                  </div>
                  <Text size={200} className="text-muted">{t.templateId}</Text>
                </TableCell>
                <TableCell>
                  <Badge size="small" appearance="outline">{t.cafPhase}</Badge>
                </TableCell>
                <TableCell>
                  {t.engagementType ? (
                    <Badge size="small" appearance="outline" color="brand">
                      {t.engagementType}
                    </Badge>
                  ) : (
                    <Text size={200} className="text-muted">{intl.formatMessage({ id: 'auditNavigator.templates.universal' })}</Text>
                  )}
                </TableCell>
                <TableCell>
                  {t.auditControls && t.auditControls.length > 0 ? (
                    <div className="tag-chip-row">
                      {t.auditControls.map(c => (
                        <Badge key={c} size="extra-small" appearance="outline">{c}</Badge>
                      ))}
                    </div>
                  ) : (
                    <Text size={200} className="text-muted">—</Text>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

// --- Capability map (issue #153 Phase D) ----------------------------------

/** One control reference of a capability, with the documents that attest it. */
interface CapabilityControlLink {
  controlId: string;
  documents: MethodologyDocument[];
}

/** A capability bridged to the project's evidence — Phase D documents → capabilities → controls. */
interface CapabilityRow {
  capability: Capability;
  /** Every control the capability references. */
  controls: CapabilityControlLink[];
  /** The subset of controls attested by at least one project document. */
  evidencedControls: CapabilityControlLink[];
}

interface CapabilityMapProps {
  rows: CapabilityRow[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  hasAnyEvidence: boolean;
  expandedCapability: string | null;
  onToggleCapability: (capabilityId: string) => void;
  onSelectDocument: (doc: MethodologyDocument) => void;
}

/**
 * Capability map — the documents → capabilities → controls bridge. Each row is a
 * GMS capability that the project's documents provide evidence for; a capability
 * is "evidenced" when at least one of its ISO control references is attested by a
 * project document. Click a row to see the per-control evidence.
 */
function CapabilityMap({
  rows,
  loading,
  error,
  onRetry,
  hasAnyEvidence,
  expandedCapability,
  onToggleCapability,
  onSelectDocument,
}: CapabilityMapProps) {
  const intl = useIntl();
  if (loading && rows.length === 0) {
    return <div className="page-loading"><Spinner size="large" label={intl.formatMessage({ id: 'auditNavigator.capabilities.loading' })} /></div>;
  }
  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>{intl.formatMessage({ id: 'auditNavigator.capabilities.error.title' })}</MessageBarTitle>
          {error}
          <div className="mt-sm">
            <Button size="small" onClick={onRetry}>{intl.formatMessage({ id: 'auditNavigator.capabilities.error.retry' })}</Button>
          </div>
        </MessageBarBody>
      </MessageBar>
    );
  }
  if (!hasAnyEvidence) {
    return (
      <div className="page-empty">
        <ClipboardCheckmarkRegular />
        <Text className="text-muted">{intl.formatMessage({ id: 'auditNavigator.capabilities.empty.noEvidence' })}</Text>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="page-empty">
        <ClipboardCheckmarkRegular />
        <Text className="text-muted">{intl.formatMessage({ id: 'auditNavigator.capabilities.empty.noBridge' })}</Text>
      </div>
    );
  }

  return (
    <>
      <Text size={300} className="text-muted mb-md" block>
        {intl.formatMessage({ id: 'auditNavigator.capabilities.description' })}
      </Text>
      <div className="table-scroll-container">
        <Table size="small" aria-label={intl.formatMessage({ id: 'auditNavigator.capabilities.tableAriaLabel' })}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ minWidth: 120 }}>{intl.formatMessage({ id: 'auditNavigator.capabilities.col.capability' })}</TableHeaderCell>
              <TableHeaderCell>{intl.formatMessage({ id: 'auditNavigator.capabilities.col.domain' })}</TableHeaderCell>
              <TableHeaderCell>{intl.formatMessage({ id: 'auditNavigator.capabilities.col.plan' })}</TableHeaderCell>
              <TableHeaderCell>{intl.formatMessage({ id: 'auditNavigator.capabilities.col.controlsEvidenced' })}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => {
              const isExpanded = expandedCapability === row.capability.id;
              return (
                <Fragment key={row.capability.id}>
                  <TableRow
                    onClick={() => onToggleCapability(row.capability.id)}
                    className="clickable"
                    aria-expanded={isExpanded}
                  >
                    <TableCell>
                      <div>
                        <Text weight="semibold">{row.capability.name}</Text>
                      </div>
                      <Text size={200} className="text-muted">{row.capability.id}</Text>
                    </TableCell>
                    <TableCell>
                      <Badge size="small" appearance="outline">{row.capability.domain}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge size="small" appearance="outline" color="brand">
                        {intl.formatMessage({ id: 'auditNavigator.capabilities.planBadge' }, { plan: row.capability.minPlan })}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Text size={200}>
                        {intl.formatMessage({ id: 'auditNavigator.capabilities.controlsCount' }, { evidenced: row.evidencedControls.length, total: row.controls.length })}
                      </Text>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <CapabilityDetail row={row} onSelectDocument={onSelectDocument} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

interface CapabilityDetailProps {
  row: CapabilityRow;
  onSelectDocument: (doc: MethodologyDocument) => void;
}

function CapabilityDetail({ row, onSelectDocument }: CapabilityDetailProps) {
  const intl = useIntl();
  return (
    <div className="audit-control-detail">
      <Text size={200} className="text-muted mb-sm" block>
        {row.capability.description}
      </Text>
      <Text size={200} weight="semibold" className="mb-sm" block>
        {intl.formatMessage({ id: 'auditNavigator.capabilityDetail.controlEvidence' }, { capabilityId: row.capability.id })}
      </Text>
      <ul className="audit-control-detail-list">
        {row.controls.map(link => (
          <li key={link.controlId}>
            <span className="flex-row gap-xs">
              <Text size={200} weight="semibold">{link.controlId}</Text>
              {link.documents.length === 0 && (
                <Badge size="extra-small" appearance="outline" color="warning">
                  {intl.formatMessage({ id: 'auditNavigator.matrix.gap' })}
                </Badge>
              )}
            </span>
            {link.documents.length > 0 && (
              <span className="flex-row flex-wrap gap-sm">
                {link.documents.map(doc => (
                  <Button
                    key={doc.id}
                    appearance="transparent"
                    size="small"
                    icon={<DocumentRegular />}
                    onClick={() => onSelectDocument(doc)}
                    className="audit-control-detail-button"
                  >
                    {doc.title}
                  </Button>
                ))}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface EmptyStateProps {
  project: Project;
  totalDocuments: number;
  loading: boolean;
}

function EmptyState({ project, totalDocuments, loading }: EmptyStateProps) {
  const intl = useIntl();
  if (loading) {
    return <div className="page-loading"><Spinner size="large" label={intl.formatMessage({ id: 'auditNavigator.emptyState.loading' })} /></div>;
  }
  return (
    <MessageBar intent="info" className="mt-md">
      <MessageBarBody>
        <MessageBarTitle>{intl.formatMessage({ id: 'auditNavigator.emptyState.title' })}</MessageBarTitle>
        {intl.formatMessage({ id: 'auditNavigator.emptyState.preamble' })}
        {totalDocuments === 0 ? (
          <> {intl.formatMessage({ id: 'auditNavigator.emptyState.noDocuments' }, { name: project.name })}</>
        ) : (
          <> {intl.formatMessage({ id: 'auditNavigator.emptyState.hasDocuments' }, { name: project.name, count: totalDocuments })}</>
        )}
      </MessageBarBody>
    </MessageBar>
  );
}

/**
 * Compare two control IDs (e.g. "A.1.1", "A.1.2", "A.10.1", "B.1.1") so the matrix
 * orders Module A above Module B and numeric segments sort numerically rather than
 * lexicographically.
 */
export function compareControlIds(a: string, b: string): number {
  const partsA = a.split('.');
  const partsB = b.split('.');
  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const sa = partsA[i] ?? '';
    const sb = partsB[i] ?? '';
    const na = Number(sa);
    const nb = Number(sb);
    const bothNumeric = !Number.isNaN(na) && !Number.isNaN(nb) && sa !== '' && sb !== '';
    if (bothNumeric) {
      if (na !== nb) return na - nb;
    } else {
      const cmp = sa.localeCompare(sb);
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
}

/**
 * Count the audit controls a document attests. Exposed for the per-document badge
 * on ProjectDetailPage.
 */
export function auditControlCount(doc: MethodologyDocument): number {
  return doc.auditControls?.length ?? 0;
}
